import {useSyncExternalStore} from 'react';

/** Per tab: a reload keeps the panel open, another tab opens its own. */
const OPEN_STORAGE_KEY = 'dms.telephony.callPanelOpen';
/** Per browser: the viewer's docked or floating pick holds for every call. */
const MODE_STORAGE_KEY = 'dms.telephony.callPanelMode';

export type CallPanelMode = 'docked' | 'floating';

interface CallPanelState {
  isOpen: boolean;
  /** Narrow screens float regardless. */
  mode: CallPanelMode;
  /** Where the call indicator renders the live call. */
  slot: HTMLElement | null;
  /** Where the call indicator renders "availability after the call". */
  footerSlot: HTMLElement | null;
  /**
   * Pairable records on screen, `${resourceId}-${recordId}`, newest last; the last one is the
   * panel's "This page" row. A record can be on screen twice (a preview and its page).
   */
  recordsOnScreen: string[];
  /** Opened from the keyboard: the panel takes focus once it shows. */
  isFocusRequested: boolean;
  /** What gets focus back when the panel closes while focused (the chip). */
  focusReturn: HTMLElement | null;
}

interface OpenOptions {
  isFocusRequested?: boolean;
  focusReturn?: HTMLElement | null;
}

const readStorage = (getStorage: () => Storage, key: string) => {
  try {
    return getStorage().getItem(key);
  } catch {
    return null;
  }
};

const writeStorage = (getStorage: () => Storage, key: string, value: string) => {
  try {
    getStorage().setItem(key, value);
  } catch {
    // Storage blocked: the state still lives for this page load.
  }
};

const createCallPanelStore = () => {
  let state: CallPanelState = {
    isOpen: readStorage(() => window.sessionStorage, OPEN_STORAGE_KEY) === 'true',
    mode:
      readStorage(() => window.localStorage, MODE_STORAGE_KEY) === 'floating'
        ? 'floating'
        : 'docked',
    slot: null,
    footerSlot: null,
    recordsOnScreen: [],
    isFocusRequested: false,
    focusReturn: null,
  };
  const listeners = new Set<VoidFunction>();

  const setState = (next: Partial<CallPanelState>) => {
    state = {...state, ...next};
    listeners.forEach((listener) => listener());
  };

  const setOpen = (isOpen: boolean) => {
    writeStorage(() => window.sessionStorage, OPEN_STORAGE_KEY, String(isOpen));
    setState({isOpen});
  };

  return {
    subscribe: (listener: VoidFunction) => {
      listeners.add(listener);

      return () => {
        listeners.delete(listener);
      };
    },
    getState: () => state,
    open: (options?: OpenOptions) => {
      setState({
        isFocusRequested: options?.isFocusRequested ?? false,
        focusReturn: options?.focusReturn ?? null,
      });
      setOpen(true);
    },
    clearFocusRequest: () => setState({isFocusRequested: false}),
    close: () => setOpen(false),
    toggleMode: () => {
      const mode = state.mode === 'docked' ? 'floating' : 'docked';

      writeStorage(() => window.localStorage, MODE_STORAGE_KEY, mode);
      setState({mode});
    },
    setSlot: (slot: HTMLElement | null) => {
      if (state.slot !== slot) {
        setState({slot});
      }
    },
    setFooterSlot: (footerSlot: HTMLElement | null) => {
      if (state.footerSlot !== footerSlot) {
        setState({footerSlot});
      }
    },
    enterRecord: (key: string) => setState({recordsOnScreen: [...state.recordsOnScreen, key]}),
    leaveRecord: (key: string) => {
      const index = state.recordsOnScreen.lastIndexOf(key);

      if (index !== -1) {
        setState({
          recordsOnScreen: state.recordsOnScreen.filter(
            (_, currentIndex) => currentIndex !== index
          ),
        });
      }
    },
  };
};

const callPanelStore = createCallPanelStore();

/**
 * The call panel's state, one per tab: shared by the top-bar indicator that opens it and renders
 * the live call into it, the panel itself, and the record pages that report themselves on screen.
 */
export function useCallPanel() {
  const state = useSyncExternalStore(callPanelStore.subscribe, callPanelStore.getState);

  return {
    state,
    currentRecordKey: state.recordsOnScreen.at(-1) ?? null,
    store: callPanelStore,
  };
}
